import React from 'react';
import { 
  Box, 
  IconButton, 
  Tooltip, 
  Typography, 
  Paper,
  useTheme,
  Stack
} from '@mui/material';
import { UserType } from '../types/auth';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// ===== EDIT THIS SECTION TO MODIFY TOOLTIP CONTENT =====
// You can add, remove, or modify the formatting suggestions here
const formattingSuggestions = [
  // First category - you can add more categories below
  {
    category: 'Numbers', // Category name
    items: [ // List of formatting guidelines in this category
      // Guideline item - you can add more items or modify existing ones
      {
        incorrect: '100,01', // Example of incorrect format
        correct: '100.01',   // Example of correct format
        description: 'All numbers should be in US number format (using period as decimal separator)' // Description of the guideline
      },
      {
        incorrect: '1,000',
        correct: '1000',
        description: 'Numbers should not have thousands separators'
      },
      {
        incorrect: ['50%', '50'],
        correct: '0.5',
        description: 'Percentages should be inputted as decimals unless otherwise specified'
      },
      {

        incorrect: ['100m', "100M", "100 million"],
        correct: '100000000',
        description: 'Numbers should be unabbreviated and fully expanded unless requested from the user. If the source document shows that numbers are "shown in millions", then you should multiply the number by 1,000,000. However, if the user requests an abbreviated number (e.g., Revenue ($m)), then you should make sure the number you submit is abbreviated to that same order of magnitude (which may still be different from the source document and require a bit of calculation).'
      }
    ]
  },
  // Dates category
  {
    category: 'Dates',
    items: [
      {
        incorrect: ['01/31/2024', '2024-01-31', '31-01-2024'],
        correct: '20240131',
        description: 'Dates should be formatted as YYYYMMDD (e.g., 20240131 for January 31, 2024)'
      }
    ]
  },
  // Values Not Found category
  {
    category: 'Values Not Found',
    items: [
      {
        description: 'If a requested value is not present in the source document, leave the relevant cell blank.'
      }
    ]
  }
  // Additional categories can be added here
];

interface FormattingGuideProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  userType?: UserType | null;
}

const FormattingGuide: React.FC<FormattingGuideProps> = ({ 
  position = 'top-right',
  userType = null
}) => {
  const theme = useTheme();
  
  // Only render for workers (reviewers), not for requesters
  if (userType !== null && userType !== UserType.WORKER) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',  // Remove the default maxWidth constraint
            width: 'auto',    // Allow width to adapt to content
            height: 'auto',   // Allow height to adapt to content
            padding: '8px',   // Add some padding around the Paper component
            backgroundColor: 'rgba(97, 97, 97, 0.2)',  // Light gray background for outer container
            borderRadius: '4px'  // Rounded corners for the outer container
          }
        }
      }}
      title={
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 2, 
                    width: 450,
                    height: 400,
                    minWidth: 350,
                    minHeight: 100,
                    maxWidth: 'none',
                    resize: 'both',
                    overflow: 'auto',
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiTypography-root': {
                      wordWrap: 'break-word',
                      maxWidth: '100%',
                      overflowWrap: 'break-word'
                    }
                  }}
                >
                <Typography variant="h6" gutterBottom>
                  Formatting Guidelines
                </Typography>
                
                {formattingSuggestions.map((category, catIndex) => (
                  <Box key={catIndex} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {category.category}
                    </Typography>
                    
                    {category.items.map((item, itemIndex) => (
                      <Box key={itemIndex} sx={{ mb: 1, pl: 1 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          • {item.description}
                        </Typography>
                        {/* Only show incorrect/correct row if both exist */}
                        {('incorrect' in item && 'correct' in item) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                              <CancelIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="text.secondary">
                                {Array.isArray(item.incorrect) ? item.incorrect.join(', ') : item.incorrect}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="text.secondary">
                                {item.correct}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Hover away to close this tooltip
                </Typography>
              </Paper>
            }
      placement="bottom"
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      enterDelay={100}
      leaveDelay={300}
      sx={{ display: 'inline-flex' }}
    >
      <IconButton 
        size="small"
        sx={{ 
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.05)',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          fontSize: '0.75rem',
          opacity: 0.7,
          userSelect: 'none'
        }}
      >
        hover here for formatting guidelines
      </Typography>
    </Stack>
  );
};

export default FormattingGuide;
